"use client";

import { useEffect, useState } from "react";
import { StatusDot, Spinner, Download } from "@/lib/icons";

const HEALTH_URL = "https://127.0.0.1:14443/health";

type Status = "checking" | "running" | "not-running";

async function probe(): Promise<{ ok: boolean; version?: string }> {
  try {
    const res = await fetch(HEALTH_URL, {
      signal: AbortSignal.timeout(2000),
      cache: "no-store",
    });
    if (!res.ok) return { ok: false };
    const data = (await res.json().catch(() => null)) as
      | { status?: string; version?: string }
      | null;
    return { ok: data?.status === "ok", version: data?.version };
  } catch {
    return { ok: false };
  }
}

/** Tiny one-line "QScanner for Mac" status for the bottom of the sidebar. */
export function MacAppStatus() {
  const [status, setStatus] = useState<Status>("checking");
  const [version, setVersion] = useState<string>();

  useEffect(() => {
    let alive = true;
    const run = () =>
      probe().then(({ ok, version }) => {
        if (!alive) return;
        setStatus(ok ? "running" : "not-running");
        setVersion(version);
      });
    run();
    const id = setInterval(run, 3000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (status === "running") {
    return (
      <div
        className="flex items-center gap-1.5 text-[11px] text-muted"
        title={`QScanner for Mac${version ? ` v${version}` : ""} is running`}
      >
        <StatusDot className="w-2.5 h-2.5 text-green shrink-0" />
        <span className="truncate">
          Mac app running{version ? ` · v${version}` : ""}
        </span>
      </div>
    );
  }

  return (
    <a
      href="/QScannerMenu.zip"
      className="flex items-center gap-1.5 text-[11px] text-muted hover:text-accent transition-colors no-underline"
      title="Download QScanner for Mac — Safari auto-extracts the app"
    >
      {status === "checking" ? (
        <Spinner className="w-2.5 h-2.5 shrink-0" />
      ) : (
        <Download className="w-3 h-3 shrink-0" />
      )}
      <span className="truncate">Get the Mac app</span>
    </a>
  );
}
